/**
 * Poças de água: com chuva ou tempestade, o jardim e o parque vão ganhando
 * poças; a chuva faz círculos nelas, e quem passa por cima levanta salpicos.
 * Quando pára de chover, secam devagar.
 *
 * Decorativo e local: cada cliente desenha as suas (nos mesmos sítios, que são
 * fixos). As contas são puras e estão testadas.
 */
import { inLake, isOutside, isWalkable, PARK_ROW, SURFACE_ROWS, TILE } from "@greenvoice/shared";
import type PhaserT from "phaser";

// ── Onde ficam ─────────────────────────────────────────────────────────────

export interface Poca {
  x: number;
  y: number;
  /** Meios-eixos da poça cheia, em px. */
  rx: number;
  ry: number;
}

function sorteio(n: number): number {
  let x = Math.imul(n ^ 0x2545f491, 0x9e3779b1);
  x ^= x >>> 15;
  x = Math.imul(x, 0x85ebca77);
  x ^= x >>> 13;
  return (x >>> 0) / 2 ** 32;
}

/** Tile sem nada por cima e com chão pisável à volta — uma poça não fica meio debaixo de uma casa. */
function sitioLivre(col: number, row: number): boolean {
  for (let dr = -1; dr <= 1; dr += 1) {
    for (let dc = -1; dc <= 1; dc += 1) {
      const c = col + dc;
      const r = row + dr;
      if (!isOutside(c, r) || inLake(c, r) || !isWalkable((c + 0.5) * TILE, (r + 0.5) * TILE)) return false;
    }
  }
  return true;
}

/** As poças: sítios fixos no jardim e no parque, espaçados uns dos outros. */
export function sitiosDasPocas(quantas = 20, espaco = 5): Poca[] {
  const candidatos: { col: number; row: number; ordem: number }[] = [];
  for (let row = 1; row < SURFACE_ROWS - 1; row += 1) {
    for (let col = 1; col < 43; col += 1) {
      if (sitioLivre(col, row)) candidatos.push({ col, row, ordem: sorteio(col * 97 + row * 13) });
    }
  }
  candidatos.sort((a, b) => a.ordem - b.ordem);
  const escolhidos: { col: number; row: number; ordem: number }[] = [];
  for (const c of candidatos) {
    if (escolhidos.length >= quantas) break;
    if (escolhidos.some((e) => Math.hypot(e.col - c.col, e.row - c.row) < espaco)) continue;
    escolhidos.push(c);
  }
  return escolhidos.map(({ col, row }) => {
    // Tamanhos bem diferentes uns dos outros: umas são um charco de dois tiles,
    // outras mal molham o chão. No asfalto do parque são maiores e mais chatas.
    const grande = sorteio(col * 31 + row * 17 + 7);
    const achatada = sorteio(col * 13 + row * 29 + 3);
    const largura = (row >= PARK_ROW ? 20 : 14) * (0.45 + grande * grande * 2.1);
    return {
      x: (col + 0.5) * TILE,
      y: (row + 0.5) * TILE,
      rx: largura,
      ry: largura * (0.3 + achatada * 0.16),
    };
  });
}

/** O ponto está dentro da poça, com a escala a que ela está agora? */
export function naPoca(p: { x: number; y: number }, poca: Poca, escala: number): boolean {
  if (escala <= 0) return false;
  const dx = (p.x - poca.x) / (poca.rx * escala);
  const dy = (p.y - poca.y) / (poca.ry * escala);
  return dx * dx + dy * dy <= 1;
}

// ── A arte ─────────────────────────────────────────────────────────────────

const T_POCA = "clima:poca";
const T_GOTINHA = "clima:gotinha";

function texturas(scene: PhaserT.Scene): void {
  if (!scene.textures.exists(T_POCA)) {
    const c = document.createElement("canvas");
    c.width = 64;
    c.height = 24;
    const ctx = c.getContext("2d");
    if (ctx) {
      // Água escura com a borda a molhar o chão, e o reflexo do céu.
      ctx.fillStyle = "rgba(20, 35, 50, 0.28)";
      ctx.beginPath();
      ctx.ellipse(32, 12, 31, 11, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "rgba(60, 95, 130, 0.62)";
      ctx.beginPath();
      ctx.ellipse(31, 12, 27, 9, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "rgba(150, 185, 215, 0.55)";
      ctx.beginPath();
      ctx.ellipse(24, 9, 12, 2.5, -0.1, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "rgba(235, 245, 255, 0.7)";
      ctx.fillRect(18, 8, 8, 1);
      scene.textures.addCanvas(T_POCA, c);
    }
  }
  if (!scene.textures.exists(T_GOTINHA)) {
    const c = document.createElement("canvas");
    c.width = 3;
    c.height = 3;
    const ctx = c.getContext("2d");
    if (ctx) {
      ctx.fillStyle = "rgba(200, 225, 250, 0.95)";
      ctx.fillRect(0, 1, 3, 1);
      ctx.fillRect(1, 0, 1, 3);
      scene.textures.addCanvas(T_GOTINHA, c);
    }
  }
}

// ── A peça no mundo ────────────────────────────────────────────────────────

/**
 * Por cima da relva e da neve do chão (-8999), por baixo dos carros e da
 * mobília rasa (`DEPTH_RUG + 2`, -8998) — uma poça não tapa um carro.
 */
const PROF_POCA = -8998.5;
const INTERVALO_SALPICO_MS = 160;

export class Pocas {
  private readonly pocas: Poca[] = sitiosDasPocas();
  private readonly imagens: PhaserT.GameObjects.Image[];
  private readonly circulos: PhaserT.GameObjects.Particles.ParticleEmitter;
  private readonly pes: PhaserT.GameObjects.Particles.ParticleEmitter;
  private readonly ultimo = new Map<string, { x: number; y: number; salpico: number }>();
  /** Quão cheias estão (0 seco, 1 cheio). */
  private cheio = 0;

  constructor(scene: PhaserT.Scene, circulo: string) {
    texturas(scene);
    this.imagens = this.pocas.map((p) =>
      scene.add
        .image(p.x, p.y, T_POCA)
        .setDisplaySize(p.rx * 2, p.ry * 2)
        .setDepth(PROF_POCA)
        .setAlpha(0)
        .setVisible(false),
    );
    const pocas = this.pocas;
    const cheio = () => this.cheio;
    // Os círculos da chuva caem dentro das poças.
    this.circulos = scene.add
      .particles(0, 0, circulo, {
        emitZone: {
          type: "random",
          source: {
            getRandomPoint: (pt: PhaserT.Types.Math.Vector2Like) => {
              const p = pocas[Math.floor(Math.random() * pocas.length)] ?? pocas[0];
              if (!p) return;
              const a = Math.random() * Math.PI * 2;
              const r = Math.sqrt(Math.random()) * 0.75 * cheio();
              pt.x = p.x + Math.cos(a) * p.rx * r;
              pt.y = p.y + Math.sin(a) * p.ry * r;
            },
          },
        },
        lifespan: 520,
        scale: { start: 0.25, end: 1 },
        alpha: { start: 0.9, end: 0 },
        frequency: 45,
        quantity: 1,
        emitting: false,
      })
      .setDepth(PROF_POCA + 1);
    // Os salpicos de quem pisa uma poça.
    this.pes = scene.add
      .particles(0, 0, T_GOTINHA, {
        lifespan: { min: 280, max: 460 },
        speed: { min: 40, max: 90 },
        angle: { min: 200, max: 340 },
        gravityY: 320,
        scale: { min: 0.8, max: 1.3 },
        alpha: { start: 1, end: 0.2 },
        emitting: false,
      })
      .setDepth(PROF_POCA + 2);
  }

  /**
   * Um frame. `chuva` é a intensidade da chuva agora (0-1); `pessoas` os pés de
   * quem está à vista, com um id para se saber quem se mexeu.
   */
  update(agora: number, dtMs: number, chuva: number, pessoas: readonly { id: string; x: number; y: number }[]): void {
    // Enchem em ~40 s de chuva e secam em ~25 s: quando o tempo muda, o chão
    // não pode ficar minutos molhado com o céu já limpo.
    const alvo = chuva > 0.3 ? 1 : 0;
    const ritmo = alvo > this.cheio ? 1 / 40 : 1 / 25;
    const passo = ritmo * (dtMs / 1000);
    this.cheio = Math.abs(alvo - this.cheio) <= passo ? alvo : this.cheio + Math.sign(alvo - this.cheio) * passo;

    const escala = 0.35 + 0.65 * this.cheio;
    this.imagens.forEach((img, i) => {
      const p = this.pocas[i];
      if (!p) return;
      img
        .setVisible(this.cheio > 0.01)
        .setAlpha(Math.min(1, this.cheio * 1.4))
        .setDisplaySize(p.rx * 2 * escala, p.ry * 2 * escala);
    });

    if (chuva > 0.05 && this.cheio > 0.15) {
      this.circulos.quantity = Math.max(1, Math.round(chuva * 3));
      if (!this.circulos.emitting) this.circulos.start();
    } else if (this.circulos.emitting) {
      this.circulos.stop();
    }

    for (const pessoa of pessoas) {
      const antes = this.ultimo.get(pessoa.id);
      const mexeu = antes !== undefined && Math.hypot(pessoa.x - antes.x, pessoa.y - antes.y) > 0.5;
      let salpico = antes?.salpico ?? 0;
      const molhadas = this.cheio > 0.15;
      if (
        mexeu &&
        molhadas &&
        agora - salpico > INTERVALO_SALPICO_MS &&
        this.pocas.some((p) => naPoca(pessoa, p, escala))
      ) {
        this.pes.explode(5, pessoa.x, pessoa.y - 2);
        salpico = agora;
      }
      this.ultimo.set(pessoa.id, { x: pessoa.x, y: pessoa.y, salpico });
    }
  }
}
